<template>
    <div>
        <h3>Facturen waar borg is betaald{{ contractId ? ' in dit contract' : '' }}</h3>
        <b-table
            :data="invoicesOnly"
            :striped="true"
            :opened-detailed="[firstDepositInvoiceId]"
            :loading="loading"
            :mobile-cards="true"
            detailed
            detail-key="id"
            :show-detail-icon="true"
        >
            <template>
                <b-table-column v-slot="props" field="number" label="Nr." width="100">
                    <router-link :to="{ name: 'InvoicesRecord', params: {type: 'verstuurd', id: props.row.id} }">
                        <strong>{{ props.row.number }}</strong>
                    </router-link>
                </b-table-column>

                <b-table-column v-slot="props" field="status" label="Status" width="140">
                    <div class="status-with-credit-badge">
                        <InvoiceStatus
                            :id="props.row.id"
                            :send_at="props.row.send_at"
                            :status="props.row.status"
                        />
                        <b-tooltip
                            v-if="_.has(props, 'row.credit_invoices') && props.row.credit_invoices.length"
                            :label="allCreditsPaid(props.row.credit_invoices) ? 'Creditfactuur betaald' : 'Creditfactuur aangemaakt, niet betaald'"
                            position="is-right"
                            class="credit-badge"
                        >
                            <b-tag :type="allCreditsPaid(props.row.credit_invoices) ? 'is-success' : 'is-dark'" size="is-small">
                                <b-icon icon="check" size="is-small" class="is-flex" />
                            </b-tag>
                        </b-tooltip>
                    </div>
                </b-table-column>

                <b-table-column v-slot="props" field="total" label="Totaal" width="110">
                    <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                    <span v-else-if="props.row.total">€&nbsp;{{ props.row.total }}</span>
                    <span v-else>-</span>
                </b-table-column>

                <b-table-column v-slot="props" field="total" label="Borg" width="110">
                    <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                    <span
                        v-else-if="getTotalDeposit(props.row.products)"
                        :class="props.row.type === 'credit' ? 'text-danger' : ''"
                    >
                        <template v-if="props.row.type === 'credit'">-&nbsp;€&nbsp;{{ getTotalDeposit(props.row.products) }}</template>
                        <template v-else>€&nbsp;{{ getTotalDeposit(props.row.products) }}</template>
                    </span>
                    <span v-else>-</span>
                </b-table-column>

                <b-table-column v-slot="props" label="PDF" width="120">
                    <a :href="'/uploads'+props.row.pdf" target="_blank" v-if="props.row.pdf" class="pdf-link">Download PDF</a>
                    <span v-else>-</span>
                </b-table-column>

                <b-table-column v-slot="props" field="invoice_date" label="Datum" width="100">
                    {{ props.row.invoice_date|humanDate }}
                </b-table-column>

                <b-table-column v-slot="props" numeric width="80">
                    <b-button tag="router-link" type="is-dark" size="is-small" :to="{ name: 'InvoicesRecord', params: {type: 'verstuurd', id: props.row.id} }" class="compact-button">Bekijken</b-button>
                </b-table-column>
            </template>

            <template #empty>
                <section class="section">
                    <div class="content has-text-grey has-text-centered">
                        <p class="margin0" v-if="loading === false">Geen facturen met borg gevonden.</p>
                        <p class="margin0" v-else>Laden...</p>
                    </div>
                </section>
            </template>

            <template #detail="props">
                <!-- Always show products table -->
                <h4>Producten</h4>

                <b-table
                    :data="props.row.products"
                    :striped="true"
                    :loading="loading"
                    :mobile-cards="true"
                >
                    <b-table-column v-slot="productProps" field="name" label="Product" width="200">
                        {{ productProps.row.name }}
                    </b-table-column>

                    <b-table-column v-slot="productProps" field="name" label="Aantal" width="100">
                        {{ productProps.row.amount || 1 }}
                    </b-table-column>

                    <b-table-column v-slot="productProps" field="deposit" label="Borg" width="100">
                        <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                        <span v-else-if="productProps.row.deposit > 0">&euro;&nbsp;{{ number_format(productProps.row.deposit * (productProps.row.amount || 1)) }}</span>
                        <span v-else>-</span>
                    </b-table-column>

                    <b-table-column v-slot="productProps" field="price" label="Totaal" width="100">
                        <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                        <span v-else-if="productProps.row.price > 0">&euro;&nbsp;{{ number_format(productProps.row.price * (productProps.row.amount || 1)) }}</span>
                        <span v-else>-</span>
                    </b-table-column>
                </b-table>

                <!-- Show credit invoices if they exist -->
                <div v-if="_.has(props, 'row.credit_invoices') && props.row.credit_invoices.length" class="margintop20">
                    <h4>Creditfacturen</h4>

                    <b-table
                        :data="props.row.credit_invoices"
                        :striped="true"
                        :loading="loading"
                        :mobile-cards="true"
                    >
                        <b-table-column v-slot="creditProps" field="number" label="Factuur nr." width="160">
                            {{ creditProps.row.number }}
                        </b-table-column>

                        <b-table-column v-slot="creditProps" field="status" label="Status" width="150">
                            <b-tag :type="_.isNull(creditProps.row.status) ? 'is-dark' : creditProps.row.status == 'paid' ? 'is-success' : _.includes(['multiple_fails', 'storno'], creditProps.row.status) ? 'is-danger' : _.includes(['one_fail'], creditProps.row.status) ? 'is-warning' : _.includes(['cancelled', 'reminder_1', 'reminder_2'], creditProps.row.status) ? 'is-warning' : 'is-info'">{{ invoiceStatus(creditProps.row.status) }}</b-tag>
                        </b-table-column>

                        <b-table-column v-slot="creditProps" field="total_price" label="Totaal" width="180">
                            <span v-if="hideSensitiveData">&euro;&nbsp;********</span>
                            <span v-else-if="creditProps.row.total" class="text-danger">-&nbsp;€&nbsp;{{ creditProps.row.total }}</span>
                            <span v-else>-</span>
                        </b-table-column>

                        <b-table-column v-slot="creditProps" field="send_at" label="Verstuurd" width="180">
                            <span v-if="creditProps.row.send_at">{{ creditProps.row.send_at | humanDate }}</span>
                            <span v-else>Nee</span>
                        </b-table-column>

                        <b-table-column v-slot="creditProps" numeric width="100">
                            <b-button tag="router-link" type="is-dark" size="is-small" :to="{ name: 'InvoicesRecord', params: {type: 'credit', id: creditProps.row.id} }">Bekijken</b-button>
                        </b-table-column>
                    </b-table>
                </div>

                <br>

                <CreateCreditInvoiceButton
                    :deposit_products="props.row.deposit_products"
                    :products="props.row.products"
                    :customerId="props.row.customer"
                    :number="props.row.number"
                    :contractId="props.row.contract?.id"
                    :hasCredits="_.has(props, 'row.credit_invoices') && !!props.row.credit_invoices.length"
                />
            </template>
        </b-table>
    </div>
</template>

<script>
import filtersMixin from '@/mixins/filterMixins'
import invoiceStatusMixin from '@/mixins/invoiceStatusMixin'
import CreateCreditInvoiceButton from '@/vue/creditinvoice/CreateCreditInvoiceButton'

export default {
    name: 'CustomerCredits',

    mixins: [filtersMixin, invoiceStatusMixin],

    components: {
        CreateCreditInvoiceButton
    },

    props: {
        customerId: {
            type: Number|String,
            required: true
        },
        // Optional: limit borg overview to a specific contract
        contractId: {
            type: Number|String,
            required: false,
            default: null
        }
    },

    data() {
        return {
            allInvoicesWithDeposit: [],
            // otherCreditInvoices: [],
            firstDepositInvoiceId: [],
            loading: false,
        }
    },

    computed: {
        hideSensitiveData() {
            return this.$store.state.hideSensitiveData
        },
        user() {
            return this.$store.state.user
        },
        // Filter out credit invoices from main list - they are shown nested under their parent invoice
        invoicesOnly() {
            return this.allInvoicesWithDeposit.filter(invoice => invoice.type !== 'credit')
        }
    },

    created() {
        if(this.customerId){
            this.fetchUserCredits()
        }
    },

    methods: {
        fetchUserCredits() {
            this.loading = true

            const params = {}
            if (this.contractId) params.contract = this.contractId
            const org = this.$store.state.user?.role === 'pit' ? this.$store.state.dataBelongsTo : this.$store.state.user?.organisation
            if (org) params.organisation = org

            axios({
                method: 'get',
                url: '/api/customer/' + this.customerId + '/deposits',
                params,
                headers: {'Authorization': 'bearer ' + this.$store.state.authToken}
            }).then((response) => {
                if(Object.keys(response.data.allInvoicesWithDeposit).length){
                    this.allInvoicesWithDeposit = Object.values(response.data.allInvoicesWithDeposit)
                }
                // if(Object.keys(response.data.otherCreditInvoices).length){
                //     this.otherCreditInvoices = Object.values(response.data.otherCreditInvoices)
                // }

                // Set ID to open first table item (using filtered list without credit invoices).
                if(this.invoicesOnly.length){
                    this.firstDepositInvoiceId = this.invoicesOnly[0].id
                }
            }).catch((error) => {
                console.error('[❌] Error while fetching customer credits ID '+this.customerId, error)
            }).then(() => {
                this.loading = false
            })

        },
        getTotalDeposit(products) {
            let total = 0

            _.each(products, (product) => {
                total += ((product.amount || 1) * product.deposit)
            })

            return this.number_format(total)
        },

        number_format(val, decimals = 2) {
            val = parseFloat(val)
            return val.toFixed(decimals)
        },

        allCreditsPaid(creditInvoices) {
            if (!creditInvoices || !creditInvoices.length) return false
            return creditInvoices.every(credit => credit.status === 'paid')
        },

    }

}
</script>

<style scoped>
.status-with-credit-badge {
    display: flex;
    align-items: center;
    gap: 4px;
}

.status-with-credit-badge .credit-badge .tag {
    height: 24px;
    padding: 0 6px;
}

.status-with-credit-badge .credit-badge .tag .icon {
    margin: 0 !important;
}
</style>
